import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
    padding: "1.5rem",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
  },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 4 },
  subtitle: { fontSize: 13, color: "#6b7280" },
  spinner: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: "1rem",
  },
  tableWrap: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
  },
  td: {
    padding: "11px 14px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
    verticalAlign: "middle",
  },
  tdSemibold: {
    padding: "11px 14px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    fontWeight: 600,
    verticalAlign: "middle",
  },
  subText: { fontSize: 11, color: "#9ca3af", marginTop: 2 },
  pill: {
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  btn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 14px",
    borderRadius: 6,
    border: "1px solid #2563EB",
    background: "transparent",
    color: "#2563EB",
    cursor: "pointer",
  },
  emptyCell: {
    padding: "2.5rem",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 13,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const pillColors = {
  pending:          { background: "#FEF3C7", color: "#92400E" },
  under_review:     { background: "#DBEAFE", color: "#1E40AF" },
  approved:         { background: "#D1FAE5", color: "#065F46" },
  rejected:         { background: "#FEE2E2", color: "#991B1B" },
  withdrawn:        { background: "#FEE2E2", color: "#991B1B" },
  property_deleted: { background: "#F3F4F6", color: "#374151" },
};

const Pill = ({ status }) => {
  const colors = pillColors[status] || { background: "#F3F4F6", color: "#374151" };
  const label = status === "property_deleted" ? "Property Deleted" : status || "unknown";
  return (
    <span style={{ ...s.pill, ...colors }}>{label.replace("_", " ")}</span>
  );
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
};

// ── Component ──────────────────────────────────────────────────────────────────
const Applications = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_ENDPOINTS.applications.getAll}?limit=50`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            ...getAuthHeaders(),
          },
        });
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        const data = await res.json();
        setApplications(data.data || []);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError(err.message || "Failed to load applications");
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", gap: 8, justifyContent: "center", padding: "4rem" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={s.spinner} />
        <span style={{ fontSize: 14, color: "#6b7280" }}>Loading applications…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.errorBanner}>
          <strong>Unable to load applications</strong>
          <p style={{ margin: "4px 0 0" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page} className="re-detail">
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .app-row:hover td { background: #f9fafb; }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Applications</div>
          <div style={s.subtitle}>View all rental applications submitted for your properties.</div>
        </div>
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        <table className="re-table" style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Application ID</th>
              <th style={s.th}>Applicant</th>
              <th style={s.th}>Property</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Submitted</th>
              <th style={{ ...s.th, width: 90 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan={6} style={s.emptyCell}>No applications found yet.</td>
              </tr>
            ) : (
              applications.map((app) => (
                <tr key={app._id} className="app-row">
                  <td data-label="Application ID" data-stacked="true" style={s.tdSemibold}>{app.applicationId || "-"}</td>
                  <td data-label="Applicant" data-stacked="true" style={s.td}>
                    <div style={{ fontWeight: 500, color: "#111827" }}>
                      {app.applicantFullName ||
                        `${app.tenant?.firstName || ""} ${app.tenant?.lastName || ""}`.trim() ||
                        "-"}
                    </div>
                    <div style={s.subText}>{app.tenant?.email || ""}</div>
                  </td>
                  <td data-label="Property" data-stacked="true" style={s.td}>
                    <div style={{ fontWeight: 500, color: "#111827" }}>
                      {app.property?.title || "Untitled property"}
                    </div>
                    <div style={s.subText}>
                      {app.property?.address
                        ? `${app.property.address.street || ""}, ${app.property.address.city || ""}`
                        : ""}
                    </div>
                  </td>
                  <td data-label="Status" data-stacked="true" style={s.td}><Pill status={app.status} /></td>
                  <td data-label="Submitted" data-stacked="true" style={s.td}>{formatDate(app.submittedAt)}</td>
                  <td data-label="" style={s.td}>
                    <button
                      style={s.btn}
                      onClick={() => navigate(app.viewPath || `/applications/${app._id}`)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Applications;
